import {
  Selectors,
  useWidgetState,
} from '@eCommerce/frontend-legacy-shared-library';
import {openURL} from '@eCommerce/frontend-legacy-shared-library';
import {PRResultCodes} from 'models/PRResultCodes';
import {useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {ROUTES} from 'routes';

import {passwordUpdate} from 'api/profile';
import PRNewPasswordEntry from 'components/PasswordReset/PRNewPasswordEntry';
import {useGetForwardUrl} from 'hooks/useGetFowardUrl';
import {useIsOpenWebView} from 'hooks/useIsOpenWebView';
import {useNavigateWithParams} from 'hooks/useNavigateWithParams';
import {AdobeEvent, useTrackEvent} from 'hooks/useTrackEvent';

const ConnectedPRNewPasswordEntry = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [containsUppercase, setContainsUppercase] = useState(false);
  const [containsLowercase, setContainsLowercase] = useState(false);
  const [containsNumber, setContainsNumber] = useState(false);
  const [containsSpecialCharacter, setContainsSpecialCharacter] =
    useState(false);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);

  const isLoggedIn = useWidgetState(Selectors.isLoggedInSelector) as boolean;
  const navigate = useNavigateWithParams();
  const forwardUrl = useGetForwardUrl() || '';
  const location = useLocation();
  const isOpenWebView = useIsOpenWebView();
  const authToken = location?.state?.passwordResetToken;
  const code = location?.state?.passwordResetCode;
  const userInformation = location?.state?.userInformation;
  const correlationId = location?.state?.correlationId;
  const hasInitialParam = forwardUrl.includes('?');

  const passwordLengthIsValid =
    password && password.length >= 13 && password.length <= 20;
  const passwordHasThreeOfFour =
    [
      containsUppercase,
      containsLowercase,
      containsNumber,
      containsSpecialCharacter,
    ].filter((value: boolean) => value === true).length >= 3;
  const passwordFieldsMatch = password === confirmPassword;
  const passwordPassesAllChecks =
    passwordHasThreeOfFour &&
    passwordLengthIsValid &&
    passwordFieldsMatch &&
    passwordErrors.length === 0;

  const passwordLengthCopy = 'Password must be 13-20 characters in length';
  const illegalCharactersCopy = 'Password contains unsupported characters';

  const {trackAdobeEvent} = useTrackEvent();

  const submitNewPassword = async () => {
    setLoading(true);
    const response = await passwordUpdate({
      authToken: authToken,
      secCode: code,
      updatedPW: password,
      correlationId: correlationId,
    });

    if (response?.data?.header?.resultCode !== PRResultCodes.SUCCESS) {
      setLoading(false);
      // invalid / expired authtoken, need to restart
      if (
        response?.data?.header?.resultCode === PRResultCodes.INVALID_AUTH_TOKEN
      ) {
        navigate(ROUTES.passwordResetInformation, {
          state: {
            passwordResetToken: '',
            passwordResetCode: '',
            reason: PRResultCodes.INVALID_AUTH_TOKEN,
            userInformation: '',
          },
        });
        // invalid / expired code, enter code again
      } else if (
        response?.data?.header?.resultCode === PRResultCodes.INVALID_CODE
      ) {
        navigate(ROUTES.passwordResetCode, {
          state: {
            passwordResetToken: authToken,
            passwordResetCode: '',
            reason: PRResultCodes.INVALID_CODE,
            userInformation: userInformation,
            correlationId: correlationId,
          },
        });
      } else {
        navigate(ROUTES.passwordResetInformation, {
          state: {
            passwordResetToken: '',
            passwordResetCode: '',
            reason: PRResultCodes.GENERIC_ERROR,
            userInformation: '',
          },
        });
      }
    } else {
      setLoading(false);
      if (isOpenWebView) {
        navigate(ROUTES.passwordResetSuccess);
      } else {
        trackAdobeEvent({
          event: AdobeEvent.pageLoad,
          page: {
            name: 'password reset update password success',
          },
        });
        if (forwardUrl) {
          openURL(
            `${forwardUrl}${hasInitialParam ? '&' : '?'}msg=passwordreset`,
          );
        } else if (isLoggedIn) {
          openURL('./rewards');
        } else {
          openURL('/rewards/signin?msg=passwordreset');
        }
      }
    }
  };

  useEffect(() => {
    // contains uppercase
    if (/.*[A-Z]+.*/.test(password)) {
      setContainsUppercase(true);
    } else {
      setContainsUppercase(false);
    }

    // contains lowercase
    if (/.*[a-z]+.*/.test(password)) {
      setContainsLowercase(true);
    } else {
      setContainsLowercase(false);
    }

    // contains numbers
    if (/.*\d+.*/.test(password)) {
      setContainsNumber(true);
    } else {
      setContainsNumber(false);
    }

    // contains special characters
    if (/.*[@'.~#$%^*\-+=!_]+.*/.test(password)) {
      setContainsSpecialCharacter(true);
    } else {
      setContainsSpecialCharacter(false);
    }

    // contains only legal characters
    if (
      /^[a-zA-Z0-9@'.~#$%^*\-+=!_]*$/.test(password) &&
      password !== undefined
    ) {
      if (passwordErrors.includes(illegalCharactersCopy)) {
        setPasswordErrors((prev) => {
          return prev.filter(
            (element: string) => element !== illegalCharactersCopy,
          );
        });
      }
    } else {
      if (!passwordErrors.includes(illegalCharactersCopy)) {
        setPasswordErrors((prev) => [...prev, illegalCharactersCopy]);
      }
    }
  }, [password, passwordErrors]);

  // on focus of the confirmPassword field, check the password field for valid length
  const checkFirstField = () => {
    if (passwordLengthIsValid) {
      setPasswordErrors((prev) => {
        return prev.filter((element: string) => element !== passwordLengthCopy);
      });
    } else {
      if (!passwordErrors.includes(passwordLengthCopy)) {
        setPasswordErrors((prev) => {
          return [...prev, passwordLengthCopy];
        });
      }
    }
  };

  return (
    <PRNewPasswordEntry
      loading={loading}
      footerOnPress={submitNewPassword}
      password={password}
      confirmPassword={confirmPassword}
      setPassword={(value: string) => setPassword(value)}
      setConfirmPassword={(value: string) => setConfirmPassword(value)}
      checkFirstField={checkFirstField}
      passwordErrors={passwordErrors}
      footerButtonDisabled={!passwordPassesAllChecks || loading}
      confirmPasswordErrors={
        !passwordFieldsMatch && password.length === confirmPassword.length
          ? ['Passwords do not match']
          : undefined
      }
      containsUppercase={containsUppercase}
      containsLowercase={containsLowercase}
      containsNumber={containsNumber}
      containsSpecialCharacter={containsSpecialCharacter}
    />
  );
};

export default ConnectedPRNewPasswordEntry;
